import { useState, useMemo } from 'react'
import {
  Box,
  Typography,
  Card,
  CardContent,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Grid,
  Paper,
  Divider,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  TextField,
  Slider,
  InputAdornment
} from '@mui/material'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import BusinessIcon from '@mui/icons-material/Business'
import EngineeringIcon from '@mui/icons-material/Engineering'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import CompareIcon from '@mui/icons-material/Compare'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import StarIcon from '@mui/icons-material/Star'
import { useNavigate } from 'react-router-dom'
import { useFilters, REGION_MAP } from '../../context/FilterContext'

// Calculate similarity score between user input and a case study
function calculateSimilarity(userInput, caseStudy) {
  let score = 0
  let maxScore = 0
  const matches = []

  // Equipment type match (required - if no match, return 0)
  if (userInput.equipmentType && caseStudy.equipment_type) {
    maxScore += 40
    if (caseStudy.equipment_type.toLowerCase().includes(userInput.equipmentType.toLowerCase()) ||
        userInput.equipmentType.toLowerCase().includes(caseStudy.equipment_type.toLowerCase())) {
      score += 40
      matches.push('Equipment type')
    } else {
      return { score: 0, maxScore: 100, matches: [] } // Required match
    }
  }

  // Industry match
  if (userInput.industry && caseStudy.industry) {
    maxScore += 20
    if (caseStudy.industry === userInput.industry) {
      score += 20
      matches.push('Industry')
    }
  }

  // Capacity within ±50%
  if (userInput.capacityTons && caseStudy.capacity_tons) {
    maxScore += 20
    const ratio = caseStudy.capacity_tons / userInput.capacityTons
    if (ratio >= 0.5 && ratio <= 2.0) {
      const capacityScore = Math.round(20 * (1 - Math.abs(1 - ratio)))
      score += capacityScore
      if (capacityScore > 10) matches.push('Similar capacity')
    }
  }

  // Equipment age within ±5 years
  if (userInput.equipmentAge && caseStudy.equipment_age) {
    maxScore += 15
    const ageDiff = Math.abs(caseStudy.equipment_age - userInput.equipmentAge)
    if (ageDiff <= 5) {
      const ageScore = Math.round(15 * (1 - ageDiff / 5))
      score += ageScore
      if (ageScore > 7) matches.push('Similar age')
    }
  }

  // Same region
  if (userInput.region && caseStudy.state) {
    maxScore += 5
    const caseRegion = Object.entries(REGION_MAP).find(([_, states]) =>
      states.includes(caseStudy.state)
    )?.[0]
    if (caseRegion === userInput.region) {
      score += 5
      matches.push('Same region')
    }
  }

  return {
    score,
    maxScore: maxScore || 100,
    percentage: maxScore > 0 ? Math.round((score / maxScore) * 100) : 0,
    matches
  }
}

export function GuidedWorkflow({ onComplete }) {
  const navigate = useNavigate()
  const { filterOptions, setIndustry, setEquipmentType, setRegion, filteredData } = useFilters()
  const [activeStep, setActiveStep] = useState(0)
  const [selections, setSelections] = useState({
    industry: '',
    equipmentType: '',
    capacityTons: 50,
    equipmentAge: 10,
    region: ''
  })

  // Find similar studies with similarity scoring
  const similarStudies = useMemo(() => {
    if (!selections.equipmentType) return []

    const studiesWithScores = filteredData
      .map(study => ({
        ...study,
        similarity: calculateSimilarity(selections, study)
      }))
      .filter(s => s.similarity.score > 0)
      .sort((a, b) => b.similarity.score - a.similarity.score)
      .slice(0, 5) // Top 5 matches

    return studiesWithScores
  }, [filteredData, selections])

  // Calculate stats for matching studies (still needed for backward compat)
  const matchingStudies = useMemo(() => {
    let matches = filteredData

    if (selections.industry) {
      matches = matches.filter(d => d.industry === selections.industry)
    }
    if (selections.equipmentType) {
      matches = matches.filter(d => d.equipment_type === selections.equipmentType)
    }

    return matches
  }, [filteredData, selections])

  // Calculate stats for matching studies
  const matchStats = useMemo(() => {
    if (matchingStudies.length === 0) return null

    const efficiencies = matchingStudies.filter(d => d.efficiency_pct).map(d => d.efficiency_pct)
    const savings = matchingStudies.filter(d => d.annual_savings).map(d => d.annual_savings)

    return {
      count: matchingStudies.length,
      avgEfficiency: efficiencies.length > 0
        ? (efficiencies.reduce((a, b) => a + b, 0) / efficiencies.length).toFixed(1)
        : 'N/A',
      minEfficiency: efficiencies.length > 0 ? Math.min(...efficiencies).toFixed(1) : 'N/A',
      maxEfficiency: efficiencies.length > 0 ? Math.max(...efficiencies).toFixed(1) : 'N/A',
      avgSavings: savings.length > 0
        ? Math.round(savings.reduce((a, b) => a + b, 0) / savings.length)
        : 'N/A',
      totalSavings: savings.length > 0
        ? savings.reduce((a, b) => a + b, 0)
        : 0
    }
  }, [matchingStudies])

  const handleNext = () => {
    setActiveStep(prev => prev + 1)
  }

  const handleBack = () => {
    setActiveStep(prev => prev - 1)
  }

  const handleSelection = (field, value) => {
    setSelections(prev => ({ ...prev, [field]: value }))
  }

  const handleViewDetails = () => {
    // Apply filters and navigate to Data Explorer
    if (selections.industry) setIndustry(selections.industry)
    if (selections.equipmentType) setEquipmentType(selections.equipmentType)
    if (selections.region) setRegion(selections.region)
    navigate('/data-explorer')
    if (onComplete) onComplete()
  }

  const steps = [
    {
      label: 'Equipment Type',
      icon: <EngineeringIcon />,
      content: (
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            What type of cooling equipment do you have?
          </Typography>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel>Equipment Type</InputLabel>
            <Select
              value={selections.equipmentType}
              label="Equipment Type"
              onChange={(e) => handleSelection('equipmentType', e.target.value)}
            >
              <MenuItem value="">Select equipment type</MenuItem>
              {filterOptions.equipmentTypes.map(type => (
                <MenuItem key={type} value={type}>{type}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            size="small"
            type="number"
            label="Capacity"
            value={selections.capacityTons}
            onChange={(e) => handleSelection('capacityTons', Number(e.target.value))}
            InputProps={{ endAdornment: <InputAdornment position="end">tons</InputAdornment> }}
            sx={{ mb: 2 }}
          />
          <Box>
            <Typography variant="caption" color="text.secondary">
              Equipment Age: {selections.equipmentAge} years
            </Typography>
            <Slider
              value={selections.equipmentAge}
              onChange={(_, v) => handleSelection('equipmentAge', v)}
              min={1}
              max={25}
              marks={[{ value: 1, label: '1' }, { value: 10, label: '10' }, { value: 25, label: '25' }]}
              valueLabelDisplay="auto"
            />
          </Box>
        </Box>
      )
    },
    {
      label: 'Industry & Location',
      icon: <BusinessIcon />,
      content: (
        <Box>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel>Industry</InputLabel>
            <Select
              value={selections.industry}
              label="Industry"
              onChange={(e) => handleSelection('industry', e.target.value)}
            >
              <MenuItem value="">Any Industry</MenuItem>
              {filterOptions.industries.map(ind => (
                <MenuItem key={ind} value={ind}>{ind.replace(/_/g, ' ')}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth size="small">
            <InputLabel>Region</InputLabel>
            <Select
              value={selections.region}
              label="Region"
              onChange={(e) => handleSelection('region', e.target.value)}
            >
              <MenuItem value="">Any Region</MenuItem>
              {Object.keys(REGION_MAP).map(region => (
                <MenuItem key={region} value={region}>{region}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      )
    },
    {
      label: 'Similar Sites',
      icon: <CompareIcon />,
      content: (
        <Box>
          {similarStudies.length > 0 ? (
            <>
              <Alert severity="success" sx={{ mb: 2 }}>
                Found <strong>{similarStudies.length} similar sites</strong> ranked by match quality
              </Alert>

              <List dense>
                {similarStudies.map((study, idx) => (
                  <Paper key={study.id} variant="outlined" sx={{ mb: 1, p: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip
                            icon={<StarIcon />}
                            label={`${study.similarity.percentage}% match`}
                            size="small"
                            color={study.similarity.percentage >= 70 ? 'success' : 'primary'}
                          />
                          <Typography variant="body2" fontWeight={600}>
                            {study.equipment_type} - {study.industry?.replace(/_/g, ' ')}
                          </Typography>
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          {study.similarity.matches.join(' • ')}
                        </Typography>
                      </Box>
                      <Box sx={{ textAlign: 'right' }}>
                        <Typography variant="h6" color="success.main">
                          {study.efficiency_pct?.toFixed(1)}%
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          efficiency gain
                        </Typography>
                      </Box>
                    </Box>
                  </Paper>
                ))}
              </List>

              {matchStats && (
                <Paper variant="outlined" sx={{ p: 2, mt: 2, bgcolor: 'grey.50' }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Summary for {matchingStudies.length} matches
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">Avg Efficiency</Typography>
                      <Typography variant="h6" color="success.main">{matchStats.avgEfficiency}%</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">Range</Typography>
                      <Typography variant="body1">{matchStats.minEfficiency}% - {matchStats.maxEfficiency}%</Typography>
                    </Grid>
                  </Grid>
                </Paper>
              )}
            </>
          ) : (
            <Alert severity="info">
              {selections.equipmentType
                ? 'No similar sites found. Try adjusting your equipment type.'
                : 'Select your equipment type to find similar sites.'}
            </Alert>
          )}
        </Box>
      )
    }
  ]

  return (
    <Card variant="outlined" sx={{ borderColor: 'primary.main' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
          <CompareIcon color="primary" />
          <Typography variant="h6">Find Sites Like Mine</Typography>
        </Box>

        <Stepper activeStep={activeStep} orientation="vertical">
          {steps.map((step, index) => (
            <Step key={step.label}>
              <StepLabel
                optional={index === steps.length - 1 ? (
                  <Typography variant="caption">Final step</Typography>
                ) : null}
              >
                {step.label}
              </StepLabel>
              <StepContent>
                {step.content}
                <Box sx={{ mt: 2 }}>
                  {index < steps.length - 1 ? (
                    <>
                      <Button
                        variant="contained"
                        onClick={handleNext}
                        size="small"
                        sx={{ mr: 1 }}
                      >
                        Continue
                      </Button>
                      <Button
                        disabled={index === 0}
                        onClick={handleBack}
                        size="small"
                      >
                        Back
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button
                        variant="contained"
                        color="success"
                        onClick={handleViewDetails}
                        startIcon={<ArrowForwardIcon />}
                        size="small"
                        sx={{ mr: 1 }}
                      >
                        View Detailed Studies
                      </Button>
                      <Button onClick={handleBack} size="small">
                        Back
                      </Button>
                    </>
                  )}
                </Box>
              </StepContent>
            </Step>
          ))}
        </Stepper>

        {activeStep === steps.length && (
          <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
            <Typography>All steps completed - redirecting to Data Explorer...</Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  )
}

// Compact version for dashboard sidebar
export function QuickFindWidget() {
  const navigate = useNavigate()
  const { filterOptions, setIndustry, setEquipmentType } = useFilters()
  const [industry, setLocalIndustry] = useState('')
  const [equipment, setLocalEquipment] = useState('')

  const handleFind = () => {
    if (industry) setIndustry(industry)
    if (equipment) setEquipmentType(equipment)
    navigate('/data-explorer')
  }

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CompareIcon fontSize="small" color="primary" />
        Quick Find
      </Typography>

      <FormControl fullWidth size="small" sx={{ mb: 1 }}>
        <InputLabel>Industry</InputLabel>
        <Select value={industry} label="Industry" onChange={(e) => setLocalIndustry(e.target.value)}>
          <MenuItem value="">Any</MenuItem>
          {filterOptions.industries.slice(0, 10).map(ind => (
            <MenuItem key={ind} value={ind}>{ind.replace(/_/g, ' ')}</MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl fullWidth size="small" sx={{ mb: 1 }}>
        <InputLabel>Equipment</InputLabel>
        <Select value={equipment} label="Equipment" onChange={(e) => setLocalEquipment(e.target.value)}>
          <MenuItem value="">Any</MenuItem>
          {filterOptions.equipmentTypes.map(type => (
            <MenuItem key={type} value={type}>{type}</MenuItem>
          ))}
        </Select>
      </FormControl>

      <Button
        fullWidth
        variant="contained"
        size="small"
        onClick={handleFind}
        startIcon={<ArrowForwardIcon />}
      >
        Find Similar Sites
      </Button>
    </Paper>
  )
}

export default GuidedWorkflow
